<template>
    <div>
        <v-card>
            <v-form @submit.prevent ref="editForm" v-model="addMedicalRecordValid">
                <v-row class="dialog-content">
                    <v-col cols="12" md="6" offset-md="3">
                        <v-row >
                            <v-col cols="12">
                                <v-text-field
                                    v-model="medicalRecord.SpecimenDisplayID"
                                    label="Specimen ID"
                                    :rules="[rules.required]"
                                >
                                </v-text-field>
                            </v-col>
                            <v-col cols="12">
                                <v-menu
                                    ref="dateMenu"
                                    v-model="dateMenu"
                                    :close-on-content-click="false"
                                    :nudge-right="40"
                                    transition="scale-transition"
                                    offset-y
                                    min-width="290px">
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-text-field
                                            v-model="displayDate"
                                            label="Date of Occurrence"
                                            prepend-icon="event"
                                            readonly
                                            v-bind="attrs"
                                            v-on="on"
                                        ></v-text-field>
                                    </template>
                                    <v-date-picker 
                                        v-model="medicalRecord.Date"
                                        :max="maxDate"
                                        scrollable
                                        @input="dateMenu = false"
                                    >
                                    </v-date-picker>
                                </v-menu>
                            </v-col>
                            <v-col cols="12">
                                <v-autocomplete
                                    :items="observationTypes"
                                    item-text="MedicalObservationTypeName"
                                    item-value="MedicalObservationTypeID"
                                    v-model="medicalRecord.MedicalObservationTypeID"
                                    :rules="[rules.selectIDRequired]"
                                    label="Medical Observation Type"
                                ></v-autocomplete>
                            </v-col>
                            <v-col cols="12">
                                <v-text-field
                                    v-if="medicalRecord.MedicalObservationTypeID===26"
                                    clearable
                                    v-model="medicalRecord.OtherTypeDescription"
                                    :rules="[rules.required]"
                                    label="Observation Description">
                                </v-text-field>
                            </v-col>
                            <v-col cols="12">
                                <v-switch label="Rest From Extraction" 
                                    v-model="medicalRecord.RestFromExtraction"
                                    color="red"
                                >
                                </v-switch>
                                <v-checkbox 
                                    label="Deceased" 
                                    v-model="medicalRecord.Deceased"
                                    @change="handleDeceasedChange"
                                >
                                </v-checkbox>
                                <v-checkbox 
                                    label="Euthanised"  
                                    v-model="medicalRecord.Euthanised"
                                    @change="handleEuthanisedChange"
                                >
                                </v-checkbox>
                            </v-col>
                            <v-col cols="12">
                                <v-textarea
                                    v-model="medicalRecord.AdditionalDetails"
                                    label="Additional Details">
                                </v-textarea>
                            </v-col>
                            <v-col cols="12">
                                
                                <label class="v-label theme--light">Images</label>
                                <MedicalRecordExistingImageList
                                    @addFile="saveImage"
                                    @removeFile="deleteImage"
                                    :closedMedicalRecord="medicalRecord.Closed"
                                    :medicalRecordID="medicalRecordID"
                                    :canAddFiles="true"
                                >
                                </MedicalRecordExistingImageList>
                                <label class="v-label theme--light">Reports</label>
                                <MedicalRecordExistingReportList
                                    @addFile="saveFile"
                                    @removeFile="deleteFile"
                                    :closedMedicalRecord="medicalRecord.Closed"
                                    :medicalRecordID="medicalRecordID"
                                    :canAddFiles="true"
                                >
                                </MedicalRecordExistingReportList>
                            </v-col>
                            <v-col cols="12">
                                <v-autocomplete
                                    :items="sortedKeywords"
                                    item-text="Description"
                                    item-value="keywordID"
                                    v-model="medicalRecord.Keywords"
                                    :rules="[rules.selectIDRequiredMultiple]"
                                    label="Keywords"
                                    multiple
                                ></v-autocomplete>
                                <v-text-field
                                    v-if="showOtherKeywordDescription"
                                    clearable
                                    required
                                    v-model="OtherKeywordDescription"
                                    :rules="[rules.required]"
                                    label="Other Keyword(s)">
                                </v-text-field>
                            </v-col>
                        </v-row>
                        <v-row  class="dialog-action-buttons">
                            <v-col cols="12" sm="2">
                                <v-btn block color="error" :disabled="saving" @click="confirmDialog=true">Cancel</v-btn>
                            </v-col>
                            <v-col cols="12" sm="3">
                                <v-btn block color="success" :disabled="!addMedicalRecordValid" :loading="saving" @click="saveMedicalRecord('save')">Save</v-btn>
                            </v-col>
                            <v-col cols="12" sm="3">
                                <v-btn block color="success" v-if="auth.hasPermissions([permissions.ACCESS_LEVELS.VET_MED_TEAM], true)" :disabled="!addMedicalRecordValid" :loading="saving" @click="showFollowupDate">Save & Followup</v-btn>
                            </v-col>
                            <v-col cols="12" sm="3">
                                <v-btn block color="success" v-if="auth.hasPermissions([permissions.ACCESS_LEVELS.VET_MED_TEAM], true)" :disabled="!addMedicalRecordValid" :loading="saving" @click="showCloseDialog">Save & Close</v-btn>
                            </v-col>
                            <v-col cols="12" v-if="saveError">
                                <v-alert text type="error">{{ saveError}}</v-alert>
                            </v-col>
                            <v-col cols="12" class="text-center" v-if="fileError">
                                <v-alert text type="error">{{fileError}}</v-alert>
                            </v-col>
                        </v-row>                            
                    </v-col>
                </v-row>
            </v-form>
        </v-card>
        <FollowupDateDialog :followupDateDialog="followupDateDialog" @save="saveMedicalRecord" @cancel="hideDateDialog">
        </FollowupDateDialog>
        <MedicalRecordCloseDialog
            :dialog="closeDialog"
            @save="saveMedicalRecord"
            @cancel="hideCloseDialog"
        >
        </MedicalRecordCloseDialog>
        <ConfirmDialog
            :dialog="confirmDialog"
            @confirm="returnToMedicalPage"
            @cancel="confirmDialog = false"
        >
        </ConfirmDialog>
    </div>
</template>

<script>
import { mapState } from 'vuex';
import { mapGetters } from 'vuex';
import moment from 'moment';
import validationRules from "../../helpers/validationrules";
import permissions from 'comunet-btg-utah-repository-shared/permissions';
import util from '@/helpers/util';
import * as auth from '../../services/auth';
import constants from 'comunet-btg-utah-repository-shared/constants';
import medicalAPI from '../../services/medicalRecordAPI';
import dailyHusbandryAPI from '../../services/dailyHusbandryAPI';
import fileService from '../../services/fileService';
import FollowupDateDialog from "./FollowupDateDialog.vue";
import MedicalRecordCloseDialog from "./CloseDialog.vue"
import ConfirmDialog from "./ConfirmReturnDialog.vue"
import MedicalRecordExistingImageList from './MedicalRecordExistingImageList.vue'
import MedicalRecordExistingReportList from "./MedicalRecordExistingReportList.vue";
// import MedicalRecordImageList from "./MedicalRecordImageList.vue"
// import MedicalRecordReportsList from './MedicalRecordReportsList.vue';

export default {
    components: {
        FollowupDateDialog,
        MedicalRecordCloseDialog,
        ConfirmDialog,
        MedicalRecordExistingImageList,
        MedicalRecordExistingReportList
        // MedicalRecordImageList,
        // MedicalRecordReportsList
    },
    props: {
        specimenDisplayID: {
            type: String,
            default: ""
        },
        medicalRecordID: {
            type: String,
            default: ""
        },
        observation: {
            type: Object,
            default: null
        }
    } ,
    data() {
        return {
            medicalRecord:{
                SpecimenDisplayID: this.specimenDisplayID,
                Date: moment().format('YYYY-MM-DD')
            },
            rules: validationRules.rules,
            permissions:permissions,
            auth:auth,
            photos:[],
            dateMenu: false,

            fileError: "",
            
            images: [],
            files: [],
            saving: false,
            followupDateDialog: false,
            closeMedicalRecord: false,
            closeDialog: false,
            confirmDialog: false,
            addMedicalRecordValid: false,
            keywords: [
               {
                    KeywordID: 0,
                    Description: "Necropsy"
               },
               {
                    KeywordID: 1,
                    Description: "Spinal Lesion"
               },
               {
                    KeywordID: 2,
                    Description: "Fracture"
               },
               {
                    KeywordID: 3,
                    Description: "Retained Ova"
               },
               {
                    KeywordID: 4,
                    Description: "Mycobacteria"
               },
               {
                    KeywordID: 5,
                    Description: "Surgery"
               },
               {
                    KeywordID: 6,
                    Description: "Spay/Neuter"
               },
               {
                    KeywordID: 7,
                    Description: "2 x Refusal"
               },
               {
                    KeywordID: 8,
                    Description: "5 x Refusal"
               },
               {
                    KeywordID: 9,
                    Description: "TF"
               },
               {
                    KeywordID: 10,
                    Description: "Other"
               }
            ],
            OtherKeywordDescription: ""
        };
    },
    computed: {
        ...mapGetters({
            medicalObservationTypes: "dailyHusbandryStore/getMedicalObservationTypes",
            
        }),
        observationTypes() {
            return [...this.$store.state.dailyHusbandryStore.medicalObservationTypes].sort(util.sortByField("MedicalObservationTypeName", ["Other"]));
        },
        sortedKeywords() {
            return [...this.keywords].sort(util.sortByField("Description", ["Other"]));
        },
        showOtherKeywordDescription() {
            return this.medicalRecord.Keywords && this.medicalRecord.Keywords.indexOf("Other") > -1;
        },
        displayDate: {
            get: function() {
                if (this.medicalRecord && this.medicalRecord.Date) {
                    return moment.utc(this.medicalRecord.Date).format(constants.DATE_FORMAT_NO_TIME);
                }
                
                return "";
            },
            set: function(newValue) {
                this.medicalRecord.Date = newValue;
            }
        },
        maxDate() {
            return moment().toISOString();
        }
    },
    watch: {
        'medicalRecord.Deceased'(nv) { if (nv) { this.medicalRecord.Euthanised = !this.medicalRecord.Deceased; }},
        'medicalRecord.Euthanised'(nv) { if (nv) { this.medicalRecord.Deceased = !this.medicalRecord.Euthanised; }},
        observation: {
            handler: function (val) {
                //console.log('handler');
                if (val) {
                    this.populateFromObservation()
                }
            },
            deep: true
        },
  },
    methods: {
        populateFromObservation(){
            this.medicalRecord.MedicalObservationTypeID = this.observation.MedicalObservationTypeID;
            this.medicalRecord.AdditionalDetails = this.observation.Details;
            this.medicalRecord.SpecimenDisplayID = this.observation.SpecimenDisplayID;
            if(this.observation.Time.length > 0) {
                this.medicalRecord.Date = moment(this.observation.Time).format('YYYY-MM-DD');
            }
        },
        showFollowupDate() {
            this.followupDateDialog = true;
        },
        hideDateDialog() {
            this.followupDateDialog = false;
        },
        showCloseDialog() {
            this.closeMedicalRecord = true;
            this.closeDialog = true;
        },
        hideCloseDialog() {
            this.closeMedicalRecord = false;
            this.closeDialog = false;
        },
        handleDeceasedChange() {
            if(this.medicalRecord.Euthanised){
                this.medicalRecord.Euthanised = 0;
            }
        },
        handleEuthanisedChange() {
            if(this.medicalRecord.Deceased){
                this.medicalRecord.Deceased = 0;
            }
        },
        formatDate(date) {
            return moment.utc(date).format(constants.DATE_FORMAT_NO_TIME);
        },
        returnToMedicalPage() {
            this.confirmDialog = false;
            this.redirectToMedicalRecords();
        },
        redirectToMedicalRecords() {
            this.$router.push({ name: 'medicalRecords'});
            this.$emit('hideDialog');
        },
        saveImage(image) {
            this.images.push(image);
        },
        deleteImage(image) {
            const removingIndex = this.images.indexOf(image)
            this.images.splice(removingIndex, 1);
        },
        saveFile(file) {
            this.files.push(file);
        },
        deleteFile(file) {
            const removingIndex = this.files.indexOf(file);
            this.files.splice(removingIndex, 1);
        },
        async saveMedicalRecord(newFollowup=true,followupDate,followupAssignedVet) {
            this.saving = true;
            this.saveError = "";
            if (followupDate !== 'save') {
                this.medicalRecord.FollowupDate = followupDate;
                this.medicalRecord.AssignedVetUser = followupAssignedVet;
            }
            if(this.closeMedicalRecord) {
                this.medicalRecord.Closed = 1;
            }
            if(this.showOtherKeywordDescription) {
                if(this.OtherKeywordDescription.trim().length > 0 ){
                    this.medicalRecord.Keywords.push(this.OtherKeywordDescription.trim());
                }
            }
            try {
                // Add the data to the database
                const response = await medicalAPI.saveMedicalRecord(this.medicalRecord);

                if(response.data.MedicalRecordID) {
                        // Add the files
                    if (this.files.length > 0) {
                        await Promise.all(this.files.map(async (file) => {
                            let document = {
                                MedicalRecordID: response.data.MedicalRecordID,
                                FileName: file.FileName
                            };

                            await fileService.uploadFileAndSaveDocument(document, file.FileName, "medicalRecords", file.ContentType, file.FileData);
                        }))
                    }
                    //Add the images
                    if(this.images.length > 0) {
                            await Promise.all(this.images.map(async (image) => {
                            let uploadImage = {
                                MedicalRecordID: response.data.MedicalRecordID,
                                ImageName: image.FileName
                            };
                            await fileService.uploadMedicalRecordImage(uploadImage, image.FileName, "medicalRecords", image.ContentType, image.FileData);
                        }))
                    }
                }
                this.$emit("hideDialog");
                this.medicalRecord = {
                    SpecimenID: this.specimenID,
                    Date: moment().format('YYYY-MM-DD')
                };
            }catch (e) {
                this.saveError = e.message;
            }finally {
                this.saving = false;
                this.hideCloseDialog();
            }
        },
        loadUsers() {
            this.$store.dispatch("userStore/loadUsers");
        },
    },
    async created() {
        this.loadUsers()
        this.medicalRecord.SpecimenDisplayID = this.$route.query.SpecimenID;
        if(this.$route.query.SpecimenDisplayID) {
            this.medicalRecord.SpecimenDisplayID = this.$route.query.SpecimenDisplayID;
        }
        this.$store.dispatch("dailyHusbandryStore/loadMedicalObservationTypes");
        if(this.observation) {
            this.populateFromObservation();
        }
        this.$nextTick(() => this.$refs.editForm.validate());
    },
}
</script>
